import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MdSearch, MdDownload, MdAddBox } from "react-icons/md";
import TablePageLayout from "components/TablePageLayout";
import Pagination from "components/Pagination";
import { getMasterCompanies, deleteMasterCompany } from "api/masterCompany.api";
import MasterCompanyTable from "components/masterCompanies/masterCompanyTable";
import { InputField } from "components/FormFields";
import CustomButton from "components/CustomButton";
import Alert from "components/common/Alert";
import SideDrawerModal from "components/SideDrawerModal";
import ViewMasterCompanyDetails from "components/masterCompanies/ViewMasterCompanyDetails";

export default function MasterCompanies() {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [alert, setAlert] = useState("");
  const [userId, setUserId] = useState("");
  const [isViewOpen,setIsViewOpen] = useState(false);
  const [selectedCompany,setSelectedCompany] = useState(null);

  const [paginationInfo, setPaginationInfo] = useState({
    totalCompanies: 0,
    totalPages: 1,
  });

  const abortControllerRef = useRef(null);

  useEffect(() => {
    const UserData = JSON.parse(localStorage.getItem("userData") || "{}");
    if (UserData._id) {
      setUserId(UserData._id);
    }
  }, []);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);
 
  const viewButton = (data) =>{
    try {
      setSelectedCompany(data);
      setIsViewOpen(true)
    } catch (error) {
      console.log("")
    }
  }
  // Fetch data using server-side pagination and search
  const fetchData = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      const res = await getMasterCompanies({
        page,
        limit,
        search: debouncedSearch,
      });

      const payload = res.data?.data || res.data || {};
      const list = payload.companies || (Array.isArray(payload) ? payload : []);

      setData(list);
      setPaginationInfo({
        totalCompanies: payload.pagination?.totalCompanies || list.length,
        totalPages: payload.pagination?.totalPages || 1,
      });
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") return;
      console.error("Failed to fetch master companies:", error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(data.map((item, idx) => item._id || idx));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleDelete = async (id) => {
    await deleteMasterCompany(id, {
      is_deleted: true,
      deleted_by: userId,
    });
    setAlert("Master Company deleted successfully");
    fetchData();
  };

  const handleExportCSV = () => {
    const exportData =
      selectedRows.length > 0
        ? data.filter((item, idx) => selectedRows.includes(item._id || idx))
        : data;

    if (!exportData.length) return;

    const headers = ["Master Company Name", "Email", "Mobile"];
    const safeStr = (val) => String(val || "").replace(/"/g, '""');

    const rows = exportData.map((c) => [
      `"${safeStr(c.masterCompanyName)}"`,
      `"${safeStr(c.masterCompanyEmail)}"`,
      `"${safeStr(c.masterCompanyMobile)}"`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `master_companies_${
        selectedRows.length > 0 ? "selected" : "all"
      }_${Date.now()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <TablePageLayout>
      <div className="grid w-full grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col">
          <InputField
            label="SEARCH"
            name="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company..."
            icon={<MdSearch className="text-lg text-gray-400" />}
          />
        </div>

        <div className="hidden lg:block"></div>

        <div className="flex flex-col items-center justify-start gap-2 sm:col-span-2 sm:flex-row sm:justify-end lg:col-span-2">
          <CustomButton
            label={
              selectedRows.length > 0
                ? `Export Selected (${selectedRows.length})`
                : "Export CSV"
            }
            icon={MdDownload}
            iconPos="left"
            variant="outline"
            color="#1A1A1A"
            borderColor="#02a9e0"
            disabled={data.length === 0}
            onClick={handleExportCSV}
          />

          <CustomButton
            label="Add Master Company"
            icon={MdAddBox}
            iconPos="right"
            variant="solid"
            color="#02a9e0"
            onClick={() => navigate("/admin/master/create")}
          />
        </div>
      </div>

      {alert && (
        <Alert type="success" message={alert} onClose={() => setAlert("")} />
      )}

      {loading ? (
        <div className="rounded-xl border bg-white p-12 text-center text-sm text-gray-500 shadow-sm">
          Loading master companies...
        </div>
      ) : (
        <div className="table-card overflow-hidden">
          <MasterCompanyTable
            data={data}
            onRefresh={fetchData}
            onDelete={handleDelete}
            selectedRows={selectedRows}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            viewButton={viewButton}
          />

          <Pagination
            currentPage={page}
            totalPages={paginationInfo.totalPages}
            totalEntries={paginationInfo.totalCompanies}
            limit={limit}
            onPageChange={(newPage) => setPage(newPage)}
            onLimitChange={(newLimit) => setLimit(newLimit)}
          />
        </div>
      )}
      <SideDrawerModal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title={selectedCompany?.masterCompanyName || "Master Company Details"}
        width="600px"
      >
        <ViewMasterCompanyDetails data={selectedCompany} />
      </SideDrawerModal>
    </TablePageLayout>
  );
}
